import React, { useState } from 'react';
import { DataVisualizer } from './DataVisualizer';
import { QueryCardData } from './QueryResultCard';
import { exportQueryResponse, exportCachedQuery, getExportLabel, isTableVisualization } from '../services/exportService';
import { useServerPagination } from '../hooks/useServerPagination';
import './QueryExpandModal.css';

const MODAL_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface QueryExpandModalProps {
    query: QueryCardData;
    onClose: () => void;
    onAskMore?: (query: QueryCardData) => void;
    onSaveWidget?: (query: QueryCardData) => void;
    pageSessionKey?: string;
}

export const QueryExpandModal: React.FC<QueryExpandModalProps> = ({
    query,
    onClose,
    onAskMore,
    onSaveWidget,
    pageSessionKey,
}) => {
    const [isExporting, setIsExporting] = useState(false);

    // Same server-side pagination the result card uses, so "expand" can browse
    // the full result set (page by page) instead of showing only page 1.
    const {
        currentPage, pageSize, totalCount, totalPages,
        pageData, isLoadingPage, pageError, canPaginate, handlePageChange,
    } = useServerPagination(query, pageSessionKey);

    if (!query.result) return null;

    const isTable = isTableVisualization(query.result.visualization);
    const showPagination = isTable && canPaginate;

    const handleExport = async () => {
        if (!query.result || isExporting) return;
        setIsExporting(true);
        try {
            const processId = query.pagination?.process_id ?? query.process_id;
            const hasMoreThanOnePage = (totalCount || 0) > (query.result.data?.length ?? 0);
            // When the answer spans more than the visible page, export the FULL
            // result set server-side (all matching rows), not just this page.
            if (pageSessionKey && processId && hasMoreThanOnePage) {
                await exportCachedQuery(
                    pageSessionKey,
                    processId,
                    query.question,
                    query.result.visualization,
                    query.result.query,
                );
            } else {
                await exportQueryResponse(
                    query.result.data,
                    query.question,
                    query.result.visualization,
                    query.result.query,
                );
            }
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="qem-overlay" onClick={onClose}>
            <div className="qem-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="qem-header">
                    <div className="qem-header-left">
                        <div className="qem-header-icon">
                            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>check_circle</span>
                        </div>
                        <div>
                            <h2 className="qem-title">{query.question}</h2>
                            <span className="qem-badge">COMPLETED</span>
                        </div>
                    </div>
                    <button className="qem-close" onClick={onClose} title="Close">
                        <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="qem-body">
                    {/* Summary */}
                    {query.result.summary && (
                        <div className="qem-summary">
                            <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#3b82f6', flexShrink: 0 }}>lightbulb</span>
                            <p>{query.result.summary}</p>
                        </div>
                    )}

                    {/* Result count — prefer the true total over the page size */}
                    {(totalCount || query.result.result_count) > 0 && (
                        <div className="qem-meta">
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>table_rows</span>
                            <span>{(totalCount || query.result.result_count).toLocaleString()} rows total</span>
                        </div>
                    )}

                    {/* Full visualization — no height cap */}
                    <div className="qem-visualization">
                        <DataVisualizer
                            data={pageData ?? query.result.data}
                            visualizationType={query.result.visualization}
                        />
                    </div>

                    {pageError && (
                        <div className="qem-meta" style={{ color: '#dc2626' }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>warning</span>
                            <span>{pageError}</span>
                        </div>
                    )}

                    {/* Server-side pagination — browse the full result set */}
                    {showPagination && (
                        <div className="qem-pagination">
                            <span className="qem-pagination-info">
                                {(() => {
                                    const start = (currentPage - 1) * pageSize + 1;
                                    const end = Math.min(currentPage * pageSize, totalCount);
                                    return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()}`;
                                })()}
                            </span>
                            <div className="qem-pagination-controls">
                                <button
                                    className="qem-btn qem-btn-close"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1 || isLoadingPage}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_left</span>
                                    Prev
                                </button>
                                <span className="qem-page-indicator">
                                    {isLoadingPage ? 'Loading…' : `${currentPage} / ${totalPages}`}
                                </span>
                                <button
                                    className="qem-btn qem-btn-close"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= totalPages || isLoadingPage}
                                >
                                    Next
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
                                </button>
                                <select
                                    className="qem-page-size-select"
                                    value={pageSize}
                                    onChange={(e) => handlePageChange(1, parseInt(e.target.value, 10))}
                                    disabled={isLoadingPage}
                                >
                                    {MODAL_PAGE_SIZE_OPTIONS.map((n) => (
                                        <option key={n} value={n}>{n} / page</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="qem-footer">
                    <button className="qem-btn qem-btn-close" onClick={onClose}>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                        Close
                    </button>
                    <button
                        className="qem-btn qem-btn-export"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                            {isExporting ? 'hourglass_empty' : (isTableVisualization(query.result.visualization) ? 'table_view' : 'picture_as_pdf')}
                        </span>
                        {isExporting ? 'Exporting...' : getExportLabel(query.result.visualization)}
                    </button>
                    {onAskMore && (
                        <button className="qem-btn qem-btn-askmore" onClick={() => { onAskMore(query); onClose(); }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chat</span>
                            Ask More
                        </button>
                    )}
                    {onSaveWidget && (
                        <button className="qem-btn qem-btn-save" onClick={() => { onSaveWidget(query); onClose(); }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>save</span>
                            Save Widget
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
